import { useTranslation } from "react-i18next";
import LOGO from "../../assets/logo.png";
import YSEALI_LOGO from "../../assets/yseali-logo.png";
import Hero from "~/components/Home/Hero";
import Students from "../../assets/icons/students.png";
import Partners from "../../assets/icons/partners.png";
import Teachers from "../../assets/icons/teachers.png";
import Languages from "../../assets/icons/languages.png";
import Community from "../../assets/icons/community.png";
import Education from "../../assets/icons/education.png";
import Family from "../../assets/icons/family.png";
import children from "../../assets/icons/children.png";
import Village from "../../assets/icons/village.png";

const impactIcons: any = {
  studentsReached: Village,
  partners: Partners,
  teachersSupported: Teachers,
  languages: Languages,
};

const serveIcons: any = {
  students: Students,
  ruralCommunities: Community,
  teachers: Teachers,
  curiousKids: children,
  families: Family,
  localEducators: Education,
};

export default function Home() {
  const { t } = useTranslation();

  return (
    <div className="p-0 max-w-[1400px] mx-auto">
      {/* Landing/Hero Section */}
      <Hero />

      {/* YSEALI Award Section */}
      <section className="py-16 bg-[var(--background-primary)]">
        <div className="max-w-6xl mx-auto">
          <div
            className="bg-gradient-to-r from-primary/10 via-primary-light/10 to-accent/10 border-2 border-primary/30 rounded-2xl p-8 md:p-12 shadow-[0_4px_6px_var(--shadow-color)]"
            style={{
              background:
                "linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(59, 130, 246, 0.1) 50%, rgba(14, 165, 233, 0.1) 100%)",
            }}
          >
            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 md:w-32 md:h-32">
                  <img
                    src={YSEALI_LOGO}
                    alt="YSEALI Logo"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        "/images/placeholder-logo.png";
                    }}
                  />
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-2xl md:text-3xl font-extrabold text-[var(--text-primary)] mb-4">
                  {t("home.ysealiAward.title")}
                </h2>
                <p className="text-[var(--text-secondary)] text-lg leading-relaxed m-0">
                  {t("home.ysealiAward.description")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="py-24 bg-[var(--background-secondary)]">
        <div className="max-w-6xl mx-auto">
          <h2 className="p-5 text-4xl md:text-5xl font-extrabold text-center mb-16 text-[var(--text-primary)] bg-gradient-to-r from-primary via-primary-light to-accent bg-clip-text text-transparent">
            {t("home.whoWeServe.title")}
          </h2>

          {/* Icon Map */}
          {/*
      Place your images in /public/serve/
      Example:
      /public/serve/students.png
      /public/serve/ruralCommunities.png
    */}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "students",
              "ruralCommunities",
              "teachers",
              "curiousKids",
              "families",
              "localEducators",
            ].map((key) => (
              <div
                key={key}
                className="bg-[var(--background-primary)] border border-[var(--border-color)] rounded-2xl p-6 text-center shadow-[0_4px_6px_var(--shadow-color)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_20px_var(--shadow-color)]"
              >
                {/* Image / Sticker */}
                <img
                  src={serveIcons[key]}
                  alt={key}
                  className="w-16 h-16 mx-auto mb-4"
                />

                {/* Label */}
                <p className="text-[var(--text-primary)] text-lg font-medium m-0">
                  {t(`home.whoWeServe.${key}`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-24 bg-[var(--background-primary)]">
        <div className="max-w-6xl mx-auto">
          <h2 className="p-5 text-4xl md:text-5xl font-extrabold text-center mb-16 text-[var(--text-primary)] bg-gradient-to-r from-primary via-primary-light to-accent bg-clip-text text-transparent">
            {t("home.impact.title")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              "studentsReached",
              "partners",
              "teachersSupported",
              "languages",
            ].map((key) => (
              <div
                key={key}
                className="bg-[var(--background-secondary)] border border-[var(--border-color)] rounded-2xl p-8 text-center shadow-[0_4px_6px_var(--shadow-color)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_20px_var(--shadow-color)]"
              >
                {/* Image / Sticker */}
                <img
                  src={impactIcons[key]}
                  alt={key}
                  className="w-14 h-14 mx-auto mb-4"
                />

                {/* Number */}
                <div className="text-4xl font-extrabold text-primary mb-4">
                  {t(`home.impact.${key}.number`)}
                </div>

                {/* Label */}
                <p className="text-[var(--text-secondary)] text-base m-0">
                  {t(`home.impact.${key}.label`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner With Us Section */}
      <section className="py-24 bg-[var(--background-secondary)]">
        <div className="max-w-6xl mx-auto">
          <h2 className="p-5 text-4xl md:text-5xl font-extrabold text-center mb-8 text-[var(--text-primary)] bg-gradient-to-r from-primary via-primary-light to-accent bg-clip-text text-transparent">
            {t("home.partners.title")}
          </h2>
          <p className="text-center text-[var(--text-secondary)] text-lg mb-12 max-w-3xl mx-auto">
            {t("home.partners.description")}
          </p>
          <div className="bg-[var(--background-primary)] border border-[var(--border-color)] rounded-2xl p-8 shadow-[0_4px_6px_var(--shadow-color)]">
            <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-6">
              {t("home.partners.typesTitle")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "schoolPrograms",
                "curriculumSupport",
                "teacherTraining",
                "technologySponsorship",
                "translationPartners",
              ].map((key) => (
                <div
                  key={key}
                  className="flex items-start gap-3 p-4 rounded-lg bg-[var(--background-secondary)] border border-[var(--border-color)]"
                >
                  <span className="text-primary text-xl">✓</span>
                  <p className="text-[var(--text-primary)] m-0">
                    {t(`home.partners.types.${key}`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="flex flex-col gap-12 py-8 ">
        <h1 className="text-[2.5rem] font-extrabold text-center  text-[var(--text-primary)]">
          {t("home.team.title")}
        </h1>

        <div
          className="p-8 rounded-xl shadow-[0_2px_4px_rgba(0,0,0,0.1)]"
          style={{
            background:
              "linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)",
            border: "1px solid rgba(37, 99, 235, 0.2)",
          }}
        >
          <h2 className="flex items-center gap-2 text-[var(--text-primary)] text-[1.75rem] mb-4">
            <span className="text-[1.5rem]">🚀</span>
            {t("home.mission.title")}
          </h2>
          <p className="text-[var(--text-primary)] text-[1.1rem] leading-relaxed">
            {t("home.mission.description")}
          </p>
        </div>

        <div
          className="p-8 rounded-xl shadow-[0_2px_4px_rgba(0,0,0,0.1)]"
          style={{
            background:
              "linear-gradient(135deg, rgba(14, 165, 233, 0.1) 0%, rgba(6, 182, 212, 0.1) 100%)",
            border: "1px solid rgba(14, 165, 233, 0.2)",
          }}
        >
          <h2 className="flex items-center gap-2 text-[var(--text-primary)] text-[1.75rem] mb-4">
            <span className="text-[1.5rem]">🔍</span>
            {t("home.vision.title")}
          </h2>
          <p className="text-[var(--text-primary)] text-[1.1rem] leading-relaxed">
            {t("home.vision.description")}
          </p>
        </div>

        <div
          className="p-8 rounded-xl shadow-[0_2px_4px_var(--shadow-color)]"
          style={{
            background:
              "linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(14, 165, 233, 0.05) 100%)",
            border: "1px solid rgba(37, 99, 235, 0.1)",
          }}
        >
          <h2 className="flex items-center gap-2 text-[var(--text-primary)] text-[1.75rem] mb-4">
            <span className="text-[1.5rem]">👥</span>
            {t("home.team.title")}
          </h2>
          <p className="text-[var(--text-secondary)] mb-8">
            {t("home.team.subtitle")}
          </p>
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8"
            style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            }}
          >
            <div className="flex items-center gap-4 p-4 rounded-lg bg-[var(--background-primary)] shadow-[0_2px_4px_rgba(0,0,0,0.1)] transition-transform duration-200 hover:-translate-y-1">
              <div className="w-[60px] h-[60px] rounded-full overflow-hidden flex-shrink-0">
                <img
                  src="/images/team/yan-aung-moe.jpg"
                  alt="Yan Aung Moe"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <h3 className="text-[var(--text-primary)] text-[1.1rem] mb-1 m-0">
                  Yan Aung Moe
                </h3>
                <p className="text-[var(--text-secondary)] text-[0.875rem] m-0">
                  {t("home.team.members.yanAungMoe")}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 rounded-lg bg-[var(--background-primary)] shadow-[0_2px_4px_rgba(0,0,0,0.1)] transition-transform duration-200 hover:-translate-y-1">
              <div className="w-[60px] h-[60px] rounded-full overflow-hidden flex-shrink-0">
                <img
                  src="/images/team/thiha-lin.jpg"
                  alt="Thiha Lin"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <h3 className="text-[var(--text-primary)] text-[1.1rem] mb-1 m-0">
                  Thiha Lin
                </h3>
                <p className="text-[var(--text-secondary)] text-[0.875rem] m-0">
                  {t("home.team.members.thihaLin")}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 rounded-lg bg-[var(--background-primary)] shadow-[0_2px_4px_rgba(0,0,0,0.1)] transition-transform duration-200 hover:-translate-y-1">
              <div className="w-[60px] h-[60px] rounded-full overflow-hidden flex-shrink-0">
                <img
                  src="/images/team/kaung-moon-san.jpg"
                  alt="Kaung Moon San"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <h3 className="text-[var(--text-primary)] text-[1.1rem] mb-1 m-0">
                  Kaung Moon San
                </h3>
                <p className="text-[var(--text-secondary)] text-[0.875rem] m-0">
                  {t("home.team.members.kaungMoonSan")}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 rounded-lg bg-[var(--background-primary)] shadow-[0_2px_4px_rgba(0,0,0,0.1)] transition-transform duration-200 hover:-translate-y-1">
              <div className="w-[60px] h-[60px] rounded-full overflow-hidden flex-shrink-0">
                <img
                  src="/images/team/kaung-set-min.jpg"
                  alt="Kaung Set Min"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <h3 className="text-[var(--text-primary)] text-[1.1rem] mb-1 m-0">
                  Kaung Set Min
                </h3>
                <p className="text-[var(--text-secondary)] text-[0.875rem] m-0">
                  {t("home.team.members.kaungSetMin")}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 rounded-lg bg-[var(--background-primary)] shadow-[0_2px_4px_rgba(0,0,0,0.1)] transition-transform duration-200 hover:-translate-y-1">
              <div className="w-[60px] h-[60px] rounded-full overflow-hidden flex-shrink-0">
                <img
                  src="/images/team/shine-thet-aung.jpg"
                  alt="Shine Thet Aung"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <h3 className="text-[var(--text-primary)] text-[1.1rem] mb-1 m-0">
                  Shine Thet Aung
                </h3>
                <p className="text-[var(--text-secondary)] text-[0.875rem] m-0">
                  {t("home.team.members.shineThetAung")}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 rounded-lg bg-[var(--background-primary)] shadow-[0_2px_4px_rgba(0,0,0,0.1)] transition-transform duration-200 hover:-translate-y-1">
              <div className="w-[60px] h-[60px] rounded-full overflow-hidden flex-shrink-0">
                <img
                  src="/images/team/thida-tun.jpg"
                  alt="Thida Tun"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <h3 className="text-[var(--text-primary)] text-[1.1rem] mb-1 m-0">
                  Thida Tun
                </h3>
                <p className="text-[var(--text-secondary)] text-[0.875rem] m-0">
                  {t("home.team.members.thidaTun")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <footer className="bg-[var(--background-secondary)] border-t border-[var(--border-color)] py-16  mt-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-16 mb-12">
            <div className="flex flex-col gap-4">
              <div className="w-16 h-16 mb-4">
                <img
                  src={LOGO}
                  alt="Awaken Logo"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "/images/placeholder-logo.png";
                  }}
                />
              </div>
              <h3 className="text-2xl font-extrabold text-[var(--text-primary)] m-0">
                {t("footer.brand.title")}
              </h3>
              <p className="text-[var(--text-secondary)] leading-relaxed m-0">
                {t("footer.brand.description")}
              </p>
              <div className="flex gap-4 mt-4">
                <a
                  href="#"
                  className="flex items-center justify-center w-10 h-10 bg-[var(--background-primary)] border border-[var(--border-color)] rounded-lg text-decoration-none transition-all duration-300 hover:bg-primary hover:border-primary hover:-translate-y-0.5"
                  aria-label="Facebook"
                >
                  <span className="text-xl">📘</span>
                </a>
                <a
                  href="#"
                  className="flex items-center justify-center w-10 h-10 bg-[var(--background-primary)] border border-[var(--border-color)] rounded-lg text-decoration-none transition-all duration-300 hover:bg-primary hover:border-primary hover:-translate-y-0.5"
                  aria-label="Twitter"
                >
                  <span className="text-xl">🐦</span>
                </a>
                <a
                  href="#"
                  className="flex items-center justify-center w-10 h-10 bg-[var(--background-primary)] border border-[var(--border-color)] rounded-lg text-decoration-none transition-all duration-300 hover:bg-primary hover:border-primary hover:-translate-y-0.5"
                  aria-label="LinkedIn"
                >
                  <span className="text-xl">💼</span>
                </a>
                <a
                  href="#"
                  className="flex items-center justify-center w-10 h-10 bg-[var(--background-primary)] border border-[var(--border-color)] rounded-lg text-decoration-none transition-all duration-300 hover:bg-primary hover:border-primary hover:-translate-y-0.5"
                  aria-label="YouTube"
                >
                  <span className="text-xl">📺</span>
                </a>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="flex flex-col gap-4">
                <h4 className="text-lg font-bold text-[var(--text-primary)] m-0">
                  {t("footer.links.products.title")}
                </h4>
                <ul className="list-none p-0 m-0 flex flex-col gap-3">
                  <li>
                    <a
                      href="/products"
                      className="text-[var(--text-secondary)] no-underline text-sm transition-colors duration-300 hover:text-primary"
                    >
                      {t("footer.links.products.padma")}
                    </a>
                  </li>
                  <li>
                    <a
                      href="/products"
                      className="text-[var(--text-secondary)] no-underline text-sm transition-colors duration-300 hover:text-primary"
                    >
                      {t("footer.links.products.tutorials")}
                    </a>
                  </li>
                  <li>
                    <a
                      href="/products"
                      className="text-[var(--text-secondary)] no-underline text-sm transition-colors duration-300 hover:text-primary"
                    >
                      {t("footer.links.products.download")}
                    </a>
                  </li>
                </ul>
              </div>

              <div className="flex flex-col gap-4">
                <h4 className="text-lg font-bold text-[var(--text-primary)] m-0">
                  {t("footer.links.resources.title")}
                </h4>
                <ul className="list-none p-0 m-0 flex flex-col gap-3">
                  <li>
                    <a
                      href="/blog"
                      className="text-[var(--text-secondary)] no-underline text-sm transition-colors duration-300 hover:text-primary"
                    >
                      {t("footer.links.resources.blog")}
                    </a>
                  </li>
                  <li>
                    <a
                      href="/news"
                      className="text-[var(--text-secondary)] no-underline text-sm transition-colors duration-300 hover:text-primary"
                    >
                      {t("footer.links.resources.news")}
                    </a>
                  </li>
                  <li>
                    <a
                      href="/services"
                      className="text-[var(--text-secondary)] no-underline text-sm transition-colors duration-300 hover:text-primary"
                    >
                      {t("footer.links.resources.services")}
                    </a>
                  </li>
                  <li>
                    <a
                      href="#"
                      className="text-[var(--text-secondary)] no-underline text-sm transition-colors duration-300 hover:text-primary"
                    >
                      {t("footer.links.resources.documentation")}
                    </a>
                  </li>
                </ul>
              </div>

              <div className="flex flex-col gap-4">
                <h4 className="text-lg font-bold text-[var(--text-primary)] m-0">
                  {t("footer.links.support.title")}
                </h4>
                <ul className="list-none p-0 m-0 flex flex-col gap-3">
                  <li>
                    <a
                      href="/contact"
                      className="text-[var(--text-secondary)] no-underline text-sm transition-colors duration-300 hover:text-primary"
                    >
                      {t("footer.links.support.contact")}
                    </a>
                  </li>
                  <li>
                    <a
                      href="#"
                      className="text-[var(--text-secondary)] no-underline text-sm transition-colors duration-300 hover:text-primary"
                    >
                      {t("footer.links.support.help")}
                    </a>
                  </li>
                  <li>
                    <a
                      href="#"
                      className="text-[var(--text-secondary)] no-underline text-sm transition-colors duration-300 hover:text-primary"
                    >
                      {t("footer.links.support.community")}
                    </a>
                  </li>
                  <li>
                    <a
                      href="#"
                      className="text-[var(--text-secondary)] no-underline text-sm transition-colors duration-300 hover:text-primary"
                    >
                      {t("footer.links.support.feedback")}
                    </a>
                  </li>
                </ul>
              </div>

              <div className="flex flex-col gap-4">
                <h4 className="text-lg font-bold text-[var(--text-primary)] m-0">
                  {t("footer.links.company.title")}
                </h4>
                <ul className="list-none p-0 m-0 flex flex-col gap-3">
                  <li>
                    <a
                      href="#"
                      className="text-[var(--text-secondary)] no-underline text-sm transition-colors duration-300 hover:text-primary"
                    >
                      {t("footer.links.company.about")}
                    </a>
                  </li>
                  <li>
                    <a
                      href="#"
                      className="text-[var(--text-secondary)] no-underline text-sm transition-colors duration-300 hover:text-primary"
                    >
                      {t("footer.links.company.team")}
                    </a>
                  </li>
                  <li>
                    <a
                      href="#"
                      className="text-[var(--text-secondary)] no-underline text-sm transition-colors duration-300 hover:text-primary"
                    >
                      {t("footer.links.company.careers")}
                    </a>
                  </li>
                  <li>
                    <a
                      href="#"
                      className="text-[var(--text-secondary)] no-underline text-sm transition-colors duration-300 hover:text-primary"
                    >
                      {t("footer.links.company.partners")}
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="border-t border-[var(--border-color)] pt-8 flex justify-between items-center flex-wrap gap-8">
            <div className="flex items-center gap-8 flex-wrap">
              <div className="text-[var(--text-secondary)] text-sm">
                <p className="m-0">
                  &copy; {new Date().getFullYear()} {t("footer.copyright.text")}
                </p>
              </div>
              <div className="flex gap-6">
                <a
                  href="#"
                  className="text-[var(--text-secondary)] no-underline text-sm transition-colors duration-300 hover:text-primary"
                >
                  {t("footer.legal.privacy")}
                </a>
                <a
                  href="#"
                  className="text-[var(--text-secondary)] no-underline text-sm transition-colors duration-300 hover:text-primary"
                >
                  {t("footer.legal.terms")}
                </a>
                <a
                  href="#"
                  className="text-[var(--text-secondary)] no-underline text-sm transition-colors duration-300 hover:text-primary"
                >
                  {t("footer.legal.cookies")}
                </a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <p className="text-[var(--text-secondary)] text-sm m-0">
                {t("footer.yseali.text")}
              </p>
              <div className="w-20 h-20">
                <img
                  src={YSEALI_LOGO}
                  alt="YSEALI Logo"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "/images/placeholder-logo.png";
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
